import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, FormControl, InputLabel,
  Select, MenuItem, Paper, Tooltip, Divider, Slide, InputAdornment,
  Skeleton, CircularProgress
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add, Edit, Delete, ExpandLess, ExpandMore,
  Search as SearchIcon, Category as CategoryIcon, Save, Close
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const TransitionUp = React.forwardRef(function TransitionUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Unidades
const MEDIDAS = [
  { value: 'und', label: 'Und' },
  { value: 'kg', label: 'Kg' },
  { value: 'g', label: 'g' },
  { value: 'l', label: 'L' },
  { value: 'ml', label: 'ml' },
  { value: 'pacote', label: 'Pacote' },
  { value: 'caixa', label: 'Caixa' },
  { value: 'garrafa', label: 'Garrafa' },
  { value: 'saco', label: 'Saco' },
  { value: 'bandeja', label: 'Bandeja' },
];

const medidaLabel = (v) => MEDIDAS.find(m => m.value === v)?.label || v || 'und';
const formatBRL = (n) =>
  (n === null || n === undefined || Number.isNaN(Number(n)))
    ? '—'
    : Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const emptyForm = {
  nome: '',
  especificacao: '',
  estoque_minimo: 0,
  unidade_medida: 'und',
  categoria_id: '',
  rende: '',
};

const FichasTecnicas = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 24;

  // Modal
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // Itens (modal)
  const [items, setItems] = useState([]);
  const [insumoOptions, setInsumoOptions] = useState([]);
  const [insumosLoading, setInsumosLoading] = useState(false);
  const [insumosQuery, setInsumosQuery] = useState('');

  // Cards
  const [expanded, setExpanded] = useState(new Set());
  const [cardItems, setCardItems] = useState({});      // { [id]: array }
  const [cardCost, setCardCost] = useState({});        // { [id]: number | undefined }
  const [cardLoading, setCardLoading] = useState({});  // { [id]: bool }

  // Debounce busca
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Categorias
  const fetchCategorias = async () => {
    try {
      const { data } = await api.get('/confereai/categorias', { params: { page: 1, per_page: 100 } });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setCategories(list);
    } catch {
      setCategories([]);
    }
  };

  // Pré-carrega itens e custo de uma ficha
  const computeCardData = async (rec) => {
    const id = rec.id;
    setCardLoading((s) => ({ ...s, [id]: true }));
    try {
      const { data } = await api.get(`/confereai/receitas/${id}/insumos`);
      const raw = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

      // Esperado do back: { id, quantidade, item: { id, nome, unidade_medida?, custo_medio?, custo_inicial? } }
      const enriched = raw.map((i) => {
        const item = i.item || i.insumo || {};
        const qtd = Number(i.quantidade ?? i.quantidade_item ?? 0);
        const unitCost = item.custo_medio ?? item.custo_inicial; // se não vier, custo fica "—"
        const unidade = item.unidade_medida ?? item.medida ?? 'und';
        const custo = unitCost != null ? qtd * Number(unitCost) : null;
        return {
          insumo_id: item.id,
          nome: item.nome,
          quantidade: qtd,
          unidade,
          custo,
        };
      });

      const total = enriched.reduce((acc, it) => acc + (Number(it.custo) || 0), 0);

      return { id, items: enriched, total: enriched.some(e => e.custo == null) ? undefined : total };
    } catch (e) {
      console.error(e);
      return { id: rec.id, items: [], total: undefined };
    } finally {
      setCardLoading((s) => ({ ...s, [rec.id]: false }));
    }
  };

  const preloadCardData = useCallback(async (list) => {
    if (!Array.isArray(list) || !list.length) return;
    const loadingMap = {};
    list.forEach(({ id }) => (loadingMap[id] = true));
    setCardLoading((s) => ({ ...s, ...loadingMap }));

    const results = await Promise.all(list.map((rec) => computeCardData(rec)));
    const itemsMap = {};
    const costMap = {};
    results.forEach(({ id, items, total }) => {
      itemsMap[id] = items;
      costMap[id] = total;
    });
    setCardItems((prev) => ({ ...prev, ...itemsMap }));
    setCardCost((prev) => ({ ...prev, ...costMap }));
  }, []);

  // Lista fichas
  const fetchFichas = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/confereai/receitas', {
        params: { q: debouncedSearch || undefined, categoria_id: categoriaId || undefined, page, per_page: perPage },
      });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setRows(list);

      // Pré-carrega itens/custos para todos os cards (custo aparece já no início)
      preloadCardData(list);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar fichas técnicas');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoriaId, page, preloadCardData]);

  useEffect(() => { fetchCategorias(); }, []);
  useEffect(() => { fetchFichas(); }, [fetchFichas]);

  // Autocomplete insumos (modal)
  const fetchInsumos = async (q = '') => {
    setInsumosLoading(true);
    try {
      const { data } = await api.get('/confereai/insumos', { params: { q: q || undefined, per_page: 12 } });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setInsumoOptions(list);
    } catch {
      setInsumoOptions([]);
    } finally {
      setInsumosLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(() => fetchInsumos(insumosQuery), 350);
    return () => clearTimeout(t);
  }, [insumosQuery]);


  // Modal abrir
  const handleOpenModal = async (rec = null) => {
    setCurrent(rec);
    if (rec) {
      setForm({
        nome: rec.nome || '',
        especificacao: rec.especificacao || '',
        estoque_minimo: rec.estoque_minimo ?? 0,
        unidade_medida: rec.unidade_medida || 'und',
        categoria_id: rec.categoria_id || '',
        rende: rec.rende ?? '',
      });
      try {
        const { data } = await api.get(`/confereai/receitas/${rec.id}/insumos`);
        const raw = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setItems(
          raw.map((i) => ({
            insumo: i.item ? { id: i.item.id, nome: i.item.nome } : null,
            quantidade: i.quantidade ?? '',
          }))
        );
      } catch {
        setItems([]);
      }
    } else {
      setForm(emptyForm);
      setItems([]);
    }
    fetchInsumos('');
    setOpen(true);
  };

  // Salvar
  const handleSave = async () => {
    try {
      if (!form.nome?.trim()) return toast.error('Informe o nome da ficha técnica');
      if (!form.unidade_medida) return toast.error('Selecione a unidade de medida');
      if (form.rende !== '' && Number(form.rende) < 0) return toast.error('Rende (kg) inválido');
      if (form.estoque_minimo !== '' && Number(form.estoque_minimo) < 0) return toast.error('Estoque mínimo inválido');

      // Monta e deduplica itens por item_id (somando quantidades)
      const map = new Map();
      for (const it of items) {
        if (!it?.insumo?.id) continue;
        const q = Number(it.quantidade);
        if (!Number.isFinite(q) || q < 0) continue;
        const id = Number(it.insumo.id);
        map.set(id, (map.get(id) ?? 0) + q);
      }
      const itensPayload = Array.from(map, ([item_id, quantidade]) => ({ item_id, quantidade }));

      // Bloqueia criar NOVA ficha sem insumos
      if (!current && itensPayload.length === 0) {
        return toast.error('Adicione ao menos 1 insumo à ficha técnica.');
      }

      const payload = {
        nome: form.nome.trim(),
        especificacao: form.especificacao?.trim() || null,
        estoque_minimo: form.estoque_minimo === '' ? 0 : Number(form.estoque_minimo),
        unidade_medida: form.unidade_medida,
        categoria_id: form.categoria_id || null,
        rende: form.rende === '' ? null : Number(form.rende),
      };

      setSaving(true);

      let res;
      if (current) {
        res = await api.put(`/confereai/receitas/${current.id}`, payload);
      } else {
        res = await api.post('/confereai/receitas', payload);
      }
      const id = current ? current.id : res.data.id;

      // Edição: remove todos os itens antigos (usando item.id) e regrava a lista atual
      try {
        const { data } = await api.get(`/confereai/receitas/${id}/insumos`);
        const old = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        await Promise.all(
          old.map((it) => {
            const itemId = it.item?.id ?? it.insumo_id ?? it.item_id;
            if (!itemId) return Promise.resolve();
            return api.delete(`/confereai/receitas/${id}/insumos/${itemId}`);
          })
        );
      } catch {
        // se der erro ao limpar, seguimos com o POST — o back deve ter upsert/normalização
      }

      if (itensPayload.length) {
        await api.post(`/confereai/receitas/${id}/insumos`, { itens: itensPayload });
      }

      toast.success('Ficha técnica salva');
      setOpen(false);
      fetchFichas(); // recarrega e já repopula custos
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar ficha técnica');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm('Excluir ficha técnica?')) return;
    try {
      await api.delete(`/confereai/receitas/${rec.id}`);
      toast.success('Excluída');
      fetchFichas();
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const toggleExpand = (rec) => {
    const id = rec.id;
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const catById = useMemo(
    () => new Map(categories.map((c) => [String(c.id), c.nome])),
    [categories]
  );

  return (
    <Box sx={{ px: { xs: 3, sm: 5 }, pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3, py: 1.5, px: 0.5,
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: 0.3
          }}
        >
          Fichas Técnicas
        </Typography>

        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>
          Criar Ficha Técnica
        </Button>
      </Box>

      {/* Filtros */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        elevation={0}
        sx={{
          p: 2.5, mb: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(250,250,250,0.7))',
          backdropFilter: 'blur(8px)'
        }}
      >
        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              label="Buscar ficha técnica"
              fullWidth
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={categoriaId}
                label="Categoria"
                onChange={(e) => { setCategoriaId(e.target.value); setPage(1); }}
              >
                <MenuItem value=""><em>Todas</em></MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Cards */}
      <Grid container spacing={3}>
        {loading ? (
          [...Array(8)].map((_, i) => (
            <Grid key={`sk-card-${i}`} item xs={12} sm={6} md={4} lg={4} xl={4}>
              <Paper sx={{ p: 4, borderRadius: '55px', border: '1px solid', borderColor: 'divider' }}>
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="rectangular" sx={{ my: 2 }} height={70} />
                <Skeleton variant="rectangular" height={120} />
              </Paper>
            </Grid>
          ))
        ) : rows.length ? (
          rows.map((rec) => {
            const open = expanded.has(rec.id);
            const custo = cardCost[rec.id];
            return (
              <Grid key={rec.id} item xs={12} sm={6} md={4} lg={4} xl={4}>
                <Paper
                  component={motion.div}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22 }}
                  elevation={0}
                  sx={{
                    borderRadius: '55px',
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    background: open ? 'rgba(255, 235, 59, 0.08)' : '#fff'
                  }}
                >
                  {/* Header do card */}
                  <Box sx={{ p: 3.5, pb: 2.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {rec.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rec.categoria_nome ?? catById.get(String(rec.categoria_id)) ?? 'Sem categoria'}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => toggleExpand(rec)}
                      sx={{
                        bgcolor: 'warning.light', color: 'warning.contrastText',
                        '&:hover': { bgcolor: 'warning.main' }
                      }}
                      size="small"
                      aria-label={open ? 'Recolher receita' : 'Expandir receita'}
                    >
                      {open ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  <Divider />

                  {/* Resumo */}
                  <Box sx={{ px: 3.5, py: 2.5, display: 'flex', gap: 4 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="overline" color="text.secondary">Rende</Typography>
                      <Typography>{rec.rende} {medidaLabel(rec.unidade_medida || 'und').toLowerCase()}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="overline" color="text.secondary">Custo</Typography>
                      <Typography sx={{ fontWeight: 700 }}>
                        {cardLoading[rec.id]
                          ? <Skeleton width={80} />
                          : custo !== undefined
                            ? formatBRL(custo)
                            : '—'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Expandido */}
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                      >
                        <Divider />
                        <Box sx={{ px: 3.5, py: 2.5 }}>
                          {rec.especificacao && (
                            <Typography sx={{ mb: 1.5 }} color="text.secondary">
                              {rec.especificacao}
                            </Typography>
                          )}

                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Produtos
                          </Typography>

                          {cardLoading[rec.id] ? (
                            <>
                              <Skeleton height={24} />
                              <Skeleton height={24} />
                              <Skeleton height={24} />
                            </>
                          ) : (
                                <Box
                                  sx={{
                                    mt: 1,
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    bgcolor: 'grey.50'
                                  }}
                                >
                                  {(cardItems[rec.id] || []).map((it, idx) => {
                                    const fullLabel = `${it.quantidade} ${it.unidade} de ${it.nome}`;
                                    return (
                                      <Box
                                        key={it.insumo_id}
                                        sx={{
                                          px: 2,
                                          py: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          columnGap: 1,
                                          ...(idx !== ((cardItems[rec.id] || []).length - 1) && {
                                            borderBottom: '1px dashed',
                                            borderColor: 'divider'
                                          })
                                        }}
                                      >
                                        <Tooltip title={fullLabel} arrow>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 auto', minWidth: 0 }}>
                                            <Box
                                              sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                opacity: 0.5,
                                                flex: '0 0 auto'
                                              }}
                                            />
                                            <Typography
                                              sx={{
                                                fontWeight: 600,
                                                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                              }}
                                            >
                                              {fullLabel}
                                            </Typography>
                                          </Box>
                                        </Tooltip>

                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            px: 0.75,
                                            py: 0.2,
                                            borderRadius: 999,
                                            bgcolor: 'background.paper',
                                            flex: '0 0 auto',
                                            ml: 1.5,
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {it.custo == null ? '—' : formatBRL(it.custo)}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}

                          <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                            <Button size="small" startIcon={<Edit />} onClick={() => handleOpenModal(rec)}>
                              Editar
                            </Button>
                            <Button size="small" color="error" startIcon={<Delete />} onClick={() => handleDelete(rec)}>
                              Excluir
                            </Button>
                          </Box>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Paper>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1px dashed', borderColor: 'divider' }}>
              <Typography color="text.secondary">Nenhuma ficha técnica</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Modal: Ficha Técnica */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={TransitionUp}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 16, scale: 0.98 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 16, scale: 0.98 },
          transition: { duration: 0.2 },
          sx: { borderRadius: 4, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon fontSize="small" /> {current ? 'Editar' : 'Nova'} Ficha Técnica
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                label="Nome da ficha"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Especificação"
                value={form.especificacao}
                onChange={(e) => setForm({ ...form, especificacao: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={form.categoria_id}
                  label="Categoria"
                  onChange={(e) => setForm({ ...form, categoria_id: e.target.value })}
                >
                  <MenuItem value=""><em>Sem categoria</em></MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                label="Estoque mínimo"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={form.estoque_minimo}
                onChange={(e) => setForm({ ...form, estoque_minimo: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Rende"
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={form.rende}
                onChange={(e) => setForm({ ...form, rende: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Unidade</InputLabel>
                <Select
                  value={form.unidade_medida}
                  label="Unidade"
                  onChange={(e) => setForm({ ...form, unidade_medida: e.target.value })}
                >
                  {MEDIDAS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="h6" mt={1}>Ingredientes</Typography>

          {items.map((it, idx) => (
            <Grid container spacing={1.5} key={idx} alignItems="center">
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  options={insumoOptions}
                  loading={insumosLoading}
                  onOpen={() => fetchInsumos('')}
                  value={it.insumo}
                  onChange={(e, v) => {
                    const copy = [...items];
                    copy[idx].insumo = v;
                    setItems(copy);
                  }}
                  onInputChange={(e, v) => setInsumosQuery(v)}
                  isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.nome || ''))}
                  filterOptions={(x) => x}
                  noOptionsText={insumosLoading ? 'Carregando...' : 'Nenhum insumo'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Insumo"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {insumosLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={9} sm={3}>
                <TextField
                  label="Qtd"
                  type="number"
                  inputProps={{ step: '0.01', min: '0' }}
                  value={it.quantidade}
                  onChange={(e) => {
                    const copy = [...items];
                    copy[idx].quantidade = e.target.value;
                    setItems(copy);
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3} sm={1} display="flex" justifyContent="flex-end">
                <Tooltip title="Remover">
                  <IconButton onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))} size="small" aria-label="Remover insumo">
                    <Delete fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          ))}
          <Box>
            <Button onClick={() => setItems((prev) => [...prev, { insumo: null, quantidade: '' }])} startIcon={<Add />}>
              Adicionar Insumo
            </Button>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<Close />} onClick={() => setOpen(false)}>Cancelar</Button>
          <Button
            startIcon={saving ? <CircularProgress size={18} /> : <Save />}
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

};

export default FichasTecnicas;